/**
 * guideService — UAT-portal PUBLIC testing-guide read client.
 *
 * Story: epic-uat-portal-001-13.
 *
 * Fetches the canonical tester guide markdown from the public, unauthenticated
 * oracle-bridge endpoint `GET /api/uat/guide` (shipped in oracle-bridge PR #564).
 * The guide is the single source of truth — it lives in the workspace and the
 * backend serves it live (internal HTML comments already stripped server-side);
 * the SPA renders whatever the backend returns and never carries a second copy.
 *
 * This endpoint is PUBLIC: it takes NO Authorization header, so — unlike the
 * board/report clients — this uses a plain `fetch`, NOT `authenticatedFetch`, and
 * never touches the tester token. A tester can read the guide with no account.
 *
 * Errors surface as a typed `GuideApiError` carrying the HTTP status (and the
 * backend's machine-readable `error` code when present), mirroring the sibling
 * services so the page can branch on status rather than parsing strings.
 *
 * Backend contract (shipped on oracle.staging.helloworlddao.com):
 *   GET /api/uat/guide
 *     → 200 { markdown: string }   (public, no auth header)
 *     | 502 { error: 'guide_unavailable' }
 */

import { getOracleBridgeUrl } from '@/utils/oracleBridge';

/**
 * Typed error for `GET /api/uat/guide` failures. `code` is the backend's
 * machine-readable `error` field ('guide_unavailable') when present, else a
 * generic fallback.
 */
export class GuideApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'GuideApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Read a JSON error body WITHOUT letting a parse failure pose as data. A
 * non-JSON / empty body yields a generic code, never a thrown parse error that
 * would mask the real HTTP status (failure-as-plausible-value: the status is the
 * source of truth — HTTP is decided by 200/502, never by a field in the body).
 */
async function readErrorCode(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

/**
 * Fetch the full tester-guide markdown from the public endpoint.
 *
 * Plain `fetch` — no bearer, no token read. The endpoint is public.
 *
 * @returns the guide markdown string (may be empty if the source is blank —
 *   the page renders a friendly "unavailable" state for that)
 * @throws GuideApiError on any non-200 (e.g. 502 guide_unavailable) or a
 *   malformed body (missing / non-string `markdown`).
 */
export async function fetchGuide(): Promise<string> {
  const response = await fetch(`${getOracleBridgeUrl()}/api/uat/guide`);

  if (!response.ok) {
    const code = await readErrorCode(response);
    throw new GuideApiError(
      'The testing guide is unavailable right now. Please try again shortly.',
      response.status,
      code,
    );
  }

  const body = (await response.json().catch(() => null)) as { markdown?: unknown } | null;
  if (!body || typeof body.markdown !== 'string') {
    throw new GuideApiError(
      'The testing guide returned an unexpected response.',
      response.status,
      'malformed_response',
    );
  }
  return body.markdown;
}
